import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileApi } from '@/shared/api/endpoints';
import type { Sex } from '@/shared/api/types';
import { describeError } from '@/shared/hooks/useAsync';
import { useSession } from '@/shared/session/useSession';
import { Banner, Button, Card, Field, SelectField } from '@/shared/ui';

/**
 * CU-02 · sexo, fecha de nacimiento y altura. Son los tres datos sin los cuales no hay TMB, y
 * por eso son obligatorios; nada más se pide, porque nada más hace falta.
 */
export function ProfilePage() {
  const { profile, refresh } = useSession();
  const navigate = useNavigate();

  const [sex, setSex] = useState<Sex | ''>(profile?.sex ?? '');
  const [birthDate, setBirthDate] = useState(profile?.birthDate ?? '');
  const [heightCm, setHeightCm] = useState(profile?.heightCm?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (sex === '') {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSaved(false);

    try {
      await profileApi.update(sex, birthDate, Number(heightCm));
      await refresh();
      setSaved(true);

      // Recién completado: el camino natural sigue en el objetivo, que es lo que desbloquea.
      if (profile?.isComplete !== true) {
        navigate('/objetivos');
      }
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="stack" onSubmit={onSubmit}>
      <h1>Tu perfil</h1>
      <p className="muted small">
        Con esto se calcula tu metabolismo basal. Sin los tres, no hay objetivo posible.
      </p>

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {saved && <Banner tone="info">Perfil guardado.</Banner>}

      <Card>
        <SelectField
          label="Sexo"
          required
          value={sex}
          onChange={(event) => setSex(event.target.value as Sex | '')}
        >
          <option value="" disabled>
            Elegí una opción
          </option>
          <option value="Female">Femenino</option>
          <option value="Male">Masculino</option>
        </SelectField>

        <Field
          label="Fecha de nacimiento"
          type="date"
          required
          value={birthDate}
          onChange={(event) => setBirthDate(event.target.value)}
        />

        <Field
          label="Altura"
          type="number"
          inputMode="numeric"
          min={100}
          max={250}
          required
          value={heightCm}
          onChange={(event) => setHeightCm(event.target.value)}
          hint="En centímetros."
        />
      </Card>

      <Button type="submit" variant="primary" block disabled={submitting}>
        {submitting ? 'Guardando…' : 'Guardar'}
      </Button>
    </form>
  );
}
